import React, { useState } from 'react';
import {
  Button,
  Icon,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';

import type { TabsProps } from './type';
import { DEFAULT_SORT_OPTIONS, VIEW_MODE_BUTTONS, VIEW_MODES } from './type';

import './Tabs.css';

/**
 * Tabs component for License Intelligence dashboard
 */
function Tabs({
  onViewModeChange,
  viewMode = VIEW_MODES.LIST,
  sortBy = 'By Annual Cost',
  onSortChange,
  sortOptions = DEFAULT_SORT_OPTIONS,
}: TabsProps): React.ReactElement {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const localize = useInvertedLocalize();

  const handleViewModeClick = (mode: 'graphical' | 'list'): void => {
    onViewModeChange?.(mode);
  };

  const handleMenuChange = (selectedOption: MenuOption): void => {
    onSortChange?.(selectedOption.label);
    setIsMenuExpanded(false);
  };

  // Custom trigger button with chevron icon
  const triggerButton = (
    <Button className='tabsDropdownTrigger'>
      {sortBy}
      <Icon kind='ChevronDown' size='small' />
    </Button>
  );

  return (
    <div className='tabsContainer'>
      <Typography kind='h2'>{localize('Overview')}</Typography>

      {viewMode === VIEW_MODES.LIST && (
        <div className='tabsDropdown'>
          <Menu
            options={sortOptions}
            trigger={triggerButton}
            isMenuExpanded={isMenuExpanded}
            setIsExpanded={setIsMenuExpanded}
            onChange={handleMenuChange}
            shouldCloseMenuOnItemClick
            isOutsideClickEnabled
            size='medium'
            placement='bottom-start'
            dataTestId='tabs-sort-menu'
          />
        </div>
      )}

      <div className='tabsActions'>
        {VIEW_MODE_BUTTONS.map((btn) => (
          <button
            key={btn.mode}
            className={`tabActionBtn tabActionBtn--${btn.position} ${
              viewMode === btn.mode ? 'tabActionBtn--active' : ''
            }`}
            aria-label={localize(btn.label)}
            type='button'
            onClick={() => handleViewModeClick(btn.mode)}
            title={localize(btn.label)}>
            <Icon kind={btn.icon} size='medium' />
          </button>
        ))}
      </div>
    </div>
  );
}

export default Tabs;
